import type { TileOf } from '@casa/model';

/**
 * The `image` tile (docs/specs/image-tile.md): a still picture, its address the source's value
 * (IM-2): a web address, a `data:image/…` URL, or a path in the app's own files.
 */

export type ImageTile = TileOf<'image'>;

/** Whether the tile reads its source: where it has a data source by that name. */
export function readsSource(tile: ImageTile) : boolean {
    return tile.dataSources[tile.options.source] !== undefined;
}

/**
 * The picture's address in a source's value (IM-2), or undefined where it is none: no value, not
 * a text, or another kind of address (`javascript:`, `file:`, an address of another host without
 * its scheme).
 */
export function imageAddressOf(value: unknown) : string | undefined {
    if (typeof value !== 'string') {
        return undefined;
    }

    const address = value.trim();
    if (/^https?:\/\/\S+$/i.test(address) || /^data:image\/[a-z0-9.+-]+[;,]/i.test(address)) {
        return address;
    }

    // A path in the app's files: relative, without a scheme or a host.
    return /^(?![a-z][a-z0-9+.-]*:)(?!\/)[\w\-./%]+$/i.test(address) ? address : undefined;
}
